import React from 'react';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

interface DataPoint {
  name: string;
  value: number;
  color?: string;
  [key: string]: any;
}

interface PieChartProps {
  data: DataPoint[];
  title?: string;
  height?: number;
  colors?: string[];
  showPercentage?: boolean;
  innerRadius?: number;
  outerRadius?: number;
}

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

// Custom tooltip
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white px-3 py-2 shadow-lg rounded-lg border border-gray-200">
        <p className="font-medium text-gray-900">{data.name}</p>
        <p className="text-sm text-gray-600">
          Count: <span className="font-semibold">{Math.round(data.value)}</span>
        </p>
      </div>
    );
  }
  return null;
};

const PieChart: React.FC<PieChartProps> = ({ 
  data, 
  title, 
  height = 300,
  colors = COLORS,
  innerRadius = 60,
  outerRadius = 100
}) => {
  // Calculate total - round values to avoid floating point issues
  const total = Math.round(data.reduce((sum, d) => sum + d.value, 0));
  
  // Filter out zero values for the pie chart
  const filteredData = data.filter(d => d.value > 0);

  // Format percentage value for display
  const formatPercentageValue = (value: number): string => {
    return value.toFixed(1);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      {title && (
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
      )}
      
      {/* Summary - Total */}
      <div className="text-center mb-2">
        <p className="text-3xl font-bold text-gray-900">{total}</p>
        <p className="text-sm text-gray-500">Total Vehicles</p>
      </div>
      
      {/* Status breakdown - always show all categories with properly formatted percentages */}
      <div className="flex flex-wrap justify-center gap-3 mb-4">
        {data.map((item, index) => (
          <div 
            key={index} 
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
              item.value > 0 ? 'bg-opacity-20' : 'bg-gray-100'
            }`}
            style={{ 
              backgroundColor: item.value > 0 ? `${item.color || colors[index % colors.length]}20` : undefined 
            }}
          >
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: item.color || colors[index % colors.length] }}
            />
            <span className={`text-sm font-medium ${item.value > 0 ? 'text-gray-800' : 'text-gray-400'}`}>
              {item.name}: {formatPercentageValue(item.value)}%
            </span>
          </div>
        ))}
      </div>
      
      {/* Pie Chart or Empty State */}
      {filteredData.length > 0 ? (
        <ResponsiveContainer width="100%" height={height - 100}>
          <RechartsPieChart>
            <Pie
              data={filteredData}
              cx="50%"
              cy="50%"
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              fill="#8884d8"
              dataKey="value"
              paddingAngle={filteredData.length > 1 ? 3 : 0}
              label={filteredData.length > 1 ? ({ percent }: any) => `${(percent * 100).toFixed(0)}%` : false}
              labelLine={filteredData.length > 1}
            >
              {filteredData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.color || colors[index % colors.length]}
                  stroke="#fff"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </RechartsPieChart>
        </ResponsiveContainer>
      ) : (
        <div className="flex flex-col items-center justify-center" style={{ height: height - 100 }}>
          <div className="w-24 h-24 rounded-full border-4 border-gray-200 flex items-center justify-center">
            <span className="text-gray-400 text-sm">No Data</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PieChart;